import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import {
  coerceToastDuration, scaleDuration, TOAST_DURATION_KEY, ToastDurationPreset,
} from './toast-duration.util';

/**
 * App-wide cache of the user's toast-duration preference. Toasts fire synchronously from
 * anywhere, so the value must be readable without an HTTP hop: it lives in a BehaviorSubject,
 * loaded once per authenticated identity and mutated optimistically. The cross-tab channel,
 * auth-gated load, and optimistic write live in UserSettingStore (shared with DateFormatService).
 */
@Injectable({ providedIn: 'root' })
export class ToastDurationService extends UserSettingStore {
  private readonly toastDuration = new BehaviorSubject<ToastDurationPreset>('system');
  readonly toastDuration$: Observable<ToastDurationPreset> = this.toastDuration.asObservable();

  /** Current preset, read synchronously by AppSnackBarService at open-time. */
  get currentToastDuration(): ToastDurationPreset {
    return this.toastDuration.value;
  }

  constructor(userSettings: UserSettingsService, store: Store, zone: NgZone) {
    super(userSettings, store, zone);
  }

  /** Scale a toast's base duration by the active preset. `undefined` result means "sticky". */
  resolveDuration(base: number | undefined): number | undefined {
    return scaleDuration(base, this.toastDuration.value);
  }

  setToastDuration(value: ToastDurationPreset): void {
    this.setPref(this.toastDuration, TOAST_DURATION_KEY, value, 'system');
  }

  protected load(userId: number): void {
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => this.emit(this.toastDuration, coerceToastDuration(settings[TOAST_DURATION_KEY])),
        error: () => this.rearmLoad(userId),
      });
  }

  protected override handlesKey(key?: string): boolean {
    return key === TOAST_DURATION_KEY;
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    if (data?.key === TOAST_DURATION_KEY) {
      this.emit(this.toastDuration, coerceToastDuration(data.value));
    }
  }
}
